import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

/**
 * Where a page sits in the app, derived from the `meta.breadcrumb` trails the
 * routes already declare. Both the breadcrumb bar and the sidebar read this, so
 * the crumb you can click and the nav item that lights up always agree.
 */

const isHomeLabel = (label) => String(label).toLowerCase() === 'home';

/** 'Home' never appears in the rendered trail, so it is dropped from keys too. */
export const withoutHome = (labels) => labels.filter((label) => !isHomeLabel(label));

/** A trail joined into a single map key. NUL cannot occur in a label. */
export const trailKey = (labels) => withoutHome(labels).join('\u0000');

export function useRouteHierarchy() {
    const route = useRoute();
    const router = useRouter();

    // Every param-free route, keyed by its own full breadcrumb trail. A trail is
    // the address of a page in the hierarchy, so keying on the whole trail keeps
    // same-named crumbs in different sections apart (the 'Settings' of
    // Settings > Users is not the 'Settings' of Profile > Settings).
    // Routes with path params are skipped: we have no id to navigate with.
    const pathsByTrail = computed(() => {
        const paths = new Map();
        router.getRoutes().forEach((candidate) => {
            if (candidate.path.includes(':')) return;
            const crumbs = candidate.meta?.breadcrumb;
            if (!Array.isArray(crumbs) || crumbs.length === 0) return;
            if (!crumbs.every((label) => typeof label === 'string')) return;
            const key = trailKey(crumbs);
            if (key && !paths.has(key)) paths.set(key, candidate.path);
        });
        return paths;
    });

    /** The current page's trail, with 'Home' stripped. */
    const trail = computed(() => {
        const crumbs = route.meta?.breadcrumb;
        return Array.isArray(crumbs) ? withoutHome(crumbs) : [];
    });

    /** The path a given crumb of the current trail points at, or null. */
    const pathForCrumb = (index) => pathsByTrail.value.get(trailKey(trail.value.slice(0, index + 1))) ?? null;

    /**
     * The current page's ancestors, nearest first: the pages whose own trail is a
     * prefix of this one. Used to decide which nav item owns the current page.
     */
    const ancestorPaths = computed(() => {
        const paths = [];
        // -1 so a page is not its own ancestor.
        for (let index = trail.value.length - 2; index >= 0; index -= 1) {
            const path = pathForCrumb(index);
            if (path && path !== route.path && !paths.includes(path)) paths.push(path);
        }
        return paths;
    });

    return { pathsByTrail, trail, pathForCrumb, ancestorPaths };
}
